import { InvoiceItem } from "@/components/common/home/InvoiceItem";
import { Box, Button, Text } from "@/ui";
import { useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ArrowLeft";
import ChevronRightIcon from "@mui/icons-material/ArrowRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { groupBy } from "lodash";
import { useState } from "react";
import Calendar from "react-calendar";
import { useNavigate, useLocation } from "react-router-dom";
import { addYears } from "date-fns";
import qs from "query-string";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import { fetchCofidocBill } from "@/api/billing/fetchCofidocBill";
import { useSystemContext } from "@/provider/common/SystemProvider";
import { useQueryParams } from "@/utils/useQueryParams";
import { formatUTC } from "@/utils/format";

export function BillingViewCpnWeb({ billing }: any) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useQueryParams();
  const { application, getUser } = useSystemContext();
  const [isDownloadingYear, setIsDownloadingYear] = useState(false);
  const [periodVisible, setPeriodVisible] = useState(false);

  const currentPeriod = params.period ? new Date(Number(params.period)) : new Date();
  const currentYear = currentPeriod.getFullYear();
  const now = new Date();
  const isCurrentYear = currentYear === now.getFullYear();

  const grouped = groupBy(billing, (b) => b.date?.getUTCFullYear());
  const bills = grouped[String(currentYear)] || [];
  const yearsWithData = new Set(Object.keys(grouped).map(Number));
  const isEarliestYear = yearsWithData.size === 0 ? true : currentYear <= Math.min(...yearsWithData);

  const shiftYear = (years: number) => {
    const nextDate = addYears(currentPeriod, years);
    const to = qs.stringify({ ...params, period: nextDate.getTime() }, { arrayFormat: "index" });
    navigate(`${location.pathname}?${to}`, { replace: true });
  };

  function base64ToBlob(base64: string, mimeType: string) {
    const binaryString = atob(base64);
    const binaryLength = binaryString.length;
    const bytes = new Uint8Array(binaryLength);

    for (let i = 0; i < binaryLength; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: mimeType });
  }

  const handleDownloadInvoicesByYear = async () => {
    if (!bills || bills.length === 0) {
      return;
    }

    setIsDownloadingYear(true);

    try {
      const zip = new JSZip();

      // Télécharger les factures de l'année avec un délai de 100ms entre chaque appel
      for (let i = 0; i < bills.length; i++) {
        const invoice = bills[i];

        try {
          const user = getUser();
          const email = user?.email || null;
          const username = user?.identifiant || null;

          const res = await fetchCofidocBill({
            id: invoice.id?.toString(),
            username,
            email,
            application,
          });

          const fileName = `Facture_${formatUTC(invoice?.date, "dd_MM_yyyy")}.pdf`;
          const pdfBlob = base64ToBlob(res?.base64, "application/pdf");

          zip.file(fileName, pdfBlob);
        } catch (error) {
          console.error(`Erreur lors du téléchargement de la facture ${invoice.id}:`, error);
        }

        // Délai de 100ms entre chaque appel (sauf pour le dernier)
        if (i < bills.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      // Générer le ZIP et le télécharger
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFileName = `Factures_Cofidoc_${currentYear}.zip`;
      saveAs(zipBlob, zipFileName);
    } catch (error) {
      console.error(`Erreur lors de la création du ZIP pour l'année ${currentYear}:`, error);
    } finally {
      setIsDownloadingYear(false);
    }
  };

  return (
    <Box width="100%" alignItems="center" flexDirection="column" height="100%" p={5} style={{ overflowY: "scroll", overflowX: "hidden" }}>
      <Box width="100%" justifyContent="center" alignItems="center" mb={4} style={{ position: "relative" }}>
        <ChevronLeftIcon
          onClick={() => !isEarliestYear && shiftYear(-1)}
          style={{
            cursor: isEarliestYear ? "default" : "pointer",
            color: isEarliestYear ? theme.palette.grey[300] : theme.palette.primary.main,
            fontSize: 40,
          }}
        />
        <Box
          onClick={() => setPeriodVisible((v) => !v)}
          flexDirection="row"
          alignItems="center"
          style={{ cursor: "pointer", margin: "0 20px" }}
        >
          <Text fontSize={24} fontWeight="700" color={theme.palette.primary.main}>
            {currentYear}
          </Text>
          <ArrowDropDownIcon style={{ color: theme.palette.primary.main }} />
        </Box>
        <ChevronRightIcon
          onClick={() => !isCurrentYear && shiftYear(1)}
          style={{
            cursor: isCurrentYear ? "default" : "pointer",
            color: isCurrentYear ? theme.palette.grey[300] : theme.palette.primary.main,
            fontSize: 40,
          }}
        />

        {periodVisible && (
          <Box
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              marginTop: "8px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              backgroundColor: "#fff",
              boxShadow: "0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 11px hsla(0, 0%, 0%, 0.1)",
              borderRadius: "5px",
              overflow: "hidden",
              zIndex: 99999,
            }}
          >
            <Calendar
              maxDetail="decade"
              tileDisabled={({ date, view }) => view === "decade" && !yearsWithData.has(date.getFullYear())}
              onChange={(c) => {
                const p = new Date(c as any as string);
                const to = qs.stringify({ ...params, period: p.getTime() }, { arrayFormat: "index" });
                navigate(`${location.pathname}?${to}`, { replace: true });
                setPeriodVisible(false);
              }}
              maxDate={new Date()}
              value={currentPeriod}
            />
            <Box p={1}>
              <Button onClick={() => setPeriodVisible(false)}>Fermer</Button>
            </Box>
          </Box>
        )}
      </Box>

      <Box width="100%" flexDirection="row" justifyContent="flex-start" alignItems="center" mb={3}>
        <Button onClick={handleDownloadInvoicesByYear} disabled={isDownloadingYear || bills.length === 0}>
          {isDownloadingYear ? `Téléchargement...` : `Télécharger les factures ${currentYear} (${bills.length})`}
        </Button>
      </Box>

      <Box width="100%" flexDirection="column">
        {bills.map((invoice) => (
          <InvoiceItem key={invoice?.id?.toString()} invoice={invoice} />
        ))}
      </Box>
    </Box>
  );
}
